import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiBookOpen, FiMessageSquare, FiSend, FiSquare, FiTrash2, FiX } from "react-icons/fi";
import "./DealDeskAi.css";

const allowedUser = (project, user) => project?.slug === "riomax" && String(user?.email || "").trim().toLowerCase() === "mayank@antrazal.com";
const sourceHref = reference => {
  const match = /^panel-(?:guide|atlas)#([a-zA-Z0-9_-]{1,180})$/.exec(String(reference || ""));
  return match ? `/backend/sow/content/riomax/documentation#${encodeURIComponent(match[1])}` : null;
};

export default function DealDeskAi({ project, user, requestJson }) {
  const permitted = allowedUser(project, user);
  const [available, setAvailable] = useState(false);
  const [opened, setOpened] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(null);
  const epoch = useRef(0);
  const dialog = useRef(null);
  const input = useRef(null);
  const trigger = useRef(null);
  const bottom = useRef(null);

  useEffect(() => {
    const current = ++epoch.current;
    const controller = new AbortController();
    active.current?.abort(); active.current = null;
    setAvailable(false); setOpened(false); setMessages([]); setQuestion(""); setError(""); setBusy(false);
    if (permitted) {
      requestJson("/backend/sow/ai/capabilities", { method: "POST", body: "{}", signal: controller.signal })
        .then(payload => { if (!controller.signal.aborted && current === epoch.current) setAvailable(payload?.data?.available === true && payload.data.scope === "riomax-documentation"); })
        .catch(() => { /* A disabled pilot must not disturb the workspace. */ });
    }
    return () => { epoch.current++; controller.abort(); active.current?.abort(); active.current = null; };
  }, [permitted, project?.id, user?.email, requestJson]);

  const close = () => {
    active.current?.abort(); active.current = null;
    dialog.current?.close();
    setBusy(false); setOpened(false); trigger.current?.focus();
  };

  useEffect(() => {
    if (opened && dialog.current && !dialog.current.open) { dialog.current.showModal(); input.current?.focus(); }
    else if (!opened && dialog.current?.open) dialog.current.close();
  }, [opened]);

  useEffect(() => {
    const element = dialog.current;
    return () => { if (element?.open) element.close(); };
  }, [available]);

  useEffect(() => { bottom.current?.scrollIntoView?.({ block: "nearest" }); }, [messages, busy]);

  const submit = async event => {
    event.preventDefault();
    if (active.current || !available || !permitted || !question.trim() || question.length > 1000) return;
    const text = question.trim();
    const controller = new AbortController();
    active.current = controller;
    const current = epoch.current;
    setBusy(true); setError("");
    try {
      const payload = await requestJson("/backend/sow/ai/ask", { method: "POST", body: JSON.stringify({ question: text }), signal: controller.signal });
      if (controller.signal.aborted || current !== epoch.current) return;
      const data = payload?.data;
      if (data?.coverage !== "approved_document_only" || !["answer", "insufficient_evidence"].includes(data.kind) || typeof data.answer !== "string" || !Array.isArray(data.sources)) throw new Error("The documentation answer could not be verified.");
      setMessages(items => [...items, { question: text, answer: data.answer, sources: data.sources }]);
      setQuestion(""); input.current?.focus();
    } catch (failure) {
      if (!controller.signal.aborted && current === epoch.current) {
        if ([401, 403, 404].includes(failure.status)) { setAvailable(false); setOpened(false); setMessages([]); setQuestion(""); }
        else setError(failure.message || "The documentation assistant is unavailable. Please try again.");
      }
    } finally {
      if (active.current === controller) { active.current = null; setBusy(false); }
    }
  };

  if (!permitted || !available) return null;
  return <>
    <button ref={trigger} type="button" className="deal-desk-ai__trigger" onClick={() => setOpened(true)} title="Ask Luna" aria-label="Ask Luna"><FiMessageSquare aria-hidden="true" /><span>Luna</span></button>
    {createPortal(<dialog ref={dialog} className="deal-desk-ai" aria-labelledby="deal-desk-ai-title" onCancel={event => { event.preventDefault(); close(); }}>
      <header className="deal-desk-ai__header">
        <FiBookOpen className="deal-desk-ai__mark" aria-hidden="true" />
        <div><h2 id="deal-desk-ai-title">Luna</h2><p>Riomax documentation</p></div>
        <button type="button" aria-label="Clear conversation" title="Clear conversation" disabled={busy || !messages.length} onClick={() => { setMessages([]); setError(""); input.current?.focus(); }}><FiTrash2 aria-hidden="true" /></button>
        <button type="button" aria-label="Close Luna" title="Close Luna" onClick={close}><FiX aria-hidden="true" /></button>
      </header>
      <div className="deal-desk-ai__messages" role="log" aria-live="polite" aria-relevant="additions">
        {!messages.length && !busy && <div className="deal-desk-ai__empty"><FiBookOpen aria-hidden="true" /><h3>Administrator Guide &amp;<br />Production Flow Atlas</h3></div>}
        {messages.map((message, n) => <article key={n} className="deal-desk-ai__exchange">
          <p className="deal-desk-ai__question">{message.question}</p>
          <div className="deal-desk-ai__answer">{message.answer}</div>
          {message.sources.length > 0 && <ul className="deal-desk-ai__sources">{message.sources.map(source => <li key={source.sourceId}>
            {sourceHref(source.reference) ? <a href={sourceHref(source.reference)} target="_blank" rel="noopener noreferrer"><FiBookOpen aria-hidden="true" />{source.title}</a> : <span>{source.title}</span>}
          </li>)}</ul>}
        </article>)}
        {busy && <div className="deal-desk-ai__skeleton" role="status" aria-label="Searching approved documentation"><span /><span /><span /></div>}
        <div ref={bottom} />
      </div>
      <form className="deal-desk-ai__composer" onSubmit={submit}>
        {error && <p role="alert" className="deal-desk-ai__error">{error}</p>}
        <label htmlFor="deal-desk-ai-question">Your question</label>
        <textarea ref={input} id="deal-desk-ai-question" value={question} maxLength={1000} disabled={busy} rows={3} onChange={event => setQuestion(event.target.value)} />
        <div className="deal-desk-ai__send-row"><span>{question.length} / 1000</span>
          {busy ? <button type="button" title="Stop request" aria-label="Stop request" onClick={() => { active.current?.abort(); active.current = null; setBusy(false); }}><FiSquare aria-hidden="true" /></button>
            : <button type="submit" title="Send question" aria-label="Send question" disabled={!question.trim()}><FiSend aria-hidden="true" /></button>}
        </div>
      </form>
    </dialog>, document.body)}
  </>;
}
